import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import EssFullWidthModal from "components/modelpopup/baseModal";
import { Button } from "components/Button";
import FilterSelect from "components/Form/filterSelect";
import { addUniformStockApi } from "api/services/uniforms";
import { toast, Bounce } from 'react-toastify';
import { format } from "date-fns";

const AddStockTakeModal = ({
  stockModal,
  setStockModal,
  uniformData,
  reloadPage
}) => {
  const [loading, setLoading] = useState(false);
  const [sizeOptions, setSizeOptions] = useState([]);

  // Check if uniform has sizes available
  const hasSizes = uniformData?.sizes && uniformData?.sizes?.length > 0;

  useEffect(() => {
    if (uniformData?.sizes) {
      const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL"];
      let _sizes = [...uniformData.sizes];
      
      _sizes.sort((a, b) => sizeOrder.indexOf(a.code) - sizeOrder.indexOf(b.code));
      
      let sizes = _sizes.map(item => {
        return {
          label: item.name + " (" + item.code + ")",
          value: item.id
        };
      });
      setSizeOptions(sizes);
    }
  }, [uniformData]);

  // Dynamic validation schema based on whether sizes are available
  const schema = yup.object().shape({
    size: hasSizes 
      ? yup.object().required("Size is required")
      : yup.object().nullable(),
    stock_date: yup.date().required("Stock date is required"),
    quantity: yup
      .number()
      .typeError("Quantity must be a number")
      .integer("Quantity must be a whole number")
      .positive("Quantity must be positive")
      .required("Quantity is required"),
    notes: yup.string()
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      size: null,
      stock_date: new Date(),
      quantity: "",
      notes: ""
    }
  });

  const handleReset = () => {
    reset({
      size: null,
      stock_date: new Date(),
      quantity: "",
      notes: ""
    });
  };

  const onSubmit = async (data) => {
    console.log(data);
    
    let payload = {
      uniform: uniformData?.id,
      size: data?.size?.value || null,
      stock_date: format(new Date(data.stock_date), "yyyy-MM-dd"),
      quantity: parseInt(data.quantity),
      notes: data.notes || ""
    };

    setLoading(true);
    
    addUniformStockApi(payload)
      .then(response => {
        setStockModal(false);
        setLoading(false);
        handleReset();
        toast.success('Stock take added successfully!', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        if (reloadPage) {
          reloadPage();
        }
      })
      .catch(e => {
        try {
          console.log(e.response?.data, "ERROR");
          setLoading(false);

          if (e.response?.data) {
            Object.entries(e.response.data).forEach(([k, v]) => {
              setError(k, {
                type: 'manual',
                message: Array.isArray(v) ? v.join(", ") : v
              });
            });
          }
        } catch (err) {
          console.log(err, "ERROR");
          let response = e.response?.data?.error || "Error adding stock take!";
          setLoading(false);
          toast.error(response, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          });
        }
      });
  };

  return (
    <>
      <EssFullWidthModal
        mask={false}
        title="Add Stock Take"
        open={stockModal}
        width={"40%"}
        footer={[]}
        onCancel={() => {
          setStockModal(false);
          handleReset();
        }}
        className="full-width-modal"
      >
        <div className="modal-content">
          <div className="modal-body">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                {/* Size Field - Required only if uniform has sizes */}
                {hasSizes && (
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label">
                      Size <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="size"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FilterSelect
                          {...register("size")}
                          isClearable={true}
                          onChange={onChange}
                          optionLabel={"label"}
                          optionValue={"value"}
                          options={sizeOptions}
                          isLoading={false}
                          value={value}
                          placeholder={"Select Size"}
                        />
                      )}
                    />
                    {errors.size && (
                      <small className="text-danger">{errors.size.message}</small>
                    )}
                  </div>
                )}

                {/* Stock Date Field */}
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label">
                    Stock Date <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="stock_date"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <DatePicker
                        selected={value}
                        onChange={onChange}
                        className="form-control datetimepicker"
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select Stock Date"
                      />
                    )}
                  />
                  {errors.stock_date && (
                    <small className="text-danger">{errors.stock_date.message}</small>
                  )}
                </div>

                {/* Quantity Field */}
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label">
                    Quantity <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter Quantity"
                    {...register("quantity")}
                  />
                  {errors.quantity && (
                    <small className="text-danger">{errors.quantity.message}</small>
                  )}
                </div>

                {/* Notes Field */}
                <div className="col-12 mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Enter notes (optional)"
                    {...register("notes")}
                  />
                  {errors.notes && (
                    <small className="text-danger">{errors.notes.message}</small>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="submit-section">
                <Button
                  loading={loading}
                  disabled={loading}
                  className="btn btn-primary submit-btn"
                  type="submit"
                  label="Add Stock Take"
                />
              </div>
            </form>
          </div>
        </div>
      </EssFullWidthModal>
    </>
  );
};

export default AddStockTakeModal;

